import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, updateDoc, setDoc, deleteDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getCurrentUserCompanyId } from "../utils/userUtils";
import { USER_ROLES } from "../utils/approvalUtils";
import { useTranslation } from 'react-i18next';

export default function UserRoleManager() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserUsername, setNewUserUsername] = useState("");
  const [newUserLoginOption, setNewUserLoginOption] = useState('email'); // 'email' | 'username' | 'both'
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserDisplayName, setNewUserDisplayName] = useState("");
  const [newUserRole, setNewUserRole] = useState(USER_ROLES.USER);
  const [newUserImageFile, setNewUserImageFile] = useState(null);
  const [newUserImagePreview, setNewUserImagePreview] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [pendingGroupChanges, setPendingGroupChanges] = useState({});
  const [company, setCompany] = useState(null);
  const [editingGroups, setEditingGroups] = useState(null);

  useEffect(() => {
    loadUsers();
    loadCompany();
  }, []);

  async function loadUsers() {
    try {
      const companyId = await getCurrentUserCompanyId();
      console.log('UserRoleManager: Current companyId:', companyId);
      if (!companyId) {
        console.warn('UserRoleManager: No companyId found');
        return;
      }

      const usersRef = collection(db, "users");
      const q = query(usersRef, where("companyId", "==", companyId));
      const snapshot = await getDocs(q);

      console.log('UserRoleManager: Found', snapshot.docs.length, 'users for company', companyId);

      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        pendingRole: null // Track pending changes
      }));

      console.log('UserRoleManager: Users data:', usersData.map(u => ({ id: u.id, email: u.email, companyId: u.companyId })));

      setUsers(usersData);
      setPendingChanges({});
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadCompany() {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        setCompany({ id: companyId, ...companyDoc.data() });
      }
    } catch (error) {
      console.error("Error loading company:", error);
    }
  }

  function handleNewUserImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert(t('users.invalidImageType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('users.imageTooLarge'));
      return;
    }
    setNewUserImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setNewUserImagePreview(reader.result);
    reader.readAsDataURL(file);
  }

  async function uploadNewUserImage(companyId, userId) {
    if (!newUserImageFile || !newUserImagePreview) return null;
    try {
      const functions = getFunctions();
      const uploadUserImage = httpsCallable(functions, 'uploadUserImage');
      const result = await uploadUserImage({ companyId, userId, dataUrl: newUserImagePreview });
      return result.data?.imageUrl || null;
    } catch (err) {
      console.error('Error uploading image:', err);
      return null;
    }
  }

  function handleRoleChange(userId, newRole) {
    // Update pending changes
    setPendingChanges(prev => ({
      ...prev,
      [userId]: newRole
    }));
    
    // Update local display
    setUsers(users.map(u => 
      u.id === userId ? { ...u, pendingRole: newRole } : u
    ));
  }

  function cancelChanges() {
    setPendingChanges({});
    setPendingGroupChanges({});
    setUsers(users.map(u => ({ ...u, pendingRole: null })));
  }

  function handleGroupToggle(userId, groupName) {
    const user = users.find(u => u.id === userId);
    const currentGroups = pendingGroupChanges[userId] || user.groups || [];
    
    let newGroups;
    if (currentGroups.includes(groupName)) {
      newGroups = currentGroups.filter(g => g !== groupName);
    } else {
      newGroups = [...currentGroups, groupName];
    }
    
    setPendingGroupChanges(prev => ({
      ...prev,
      [userId]: newGroups
    }));
  }

  async function saveAllChanges() {
    if (Object.keys(pendingChanges).length === 0 && Object.keys(pendingGroupChanges).length === 0) {
      alert(t('users.noChangesToSave'));
      return;
    }

    setUpdating("all");

    try {
      const updatePromises = [];
      
      // Update roles
      Object.entries(pendingChanges).forEach(([userId, newRole]) => {
        const userRef = doc(db, "users", userId);
        updatePromises.push(updateDoc(userRef, {
          role: newRole,
          updatedAt: new Date()
        }));
      });

      // Update groups
      Object.entries(pendingGroupChanges).forEach(([userId, newGroups]) => {
        const userRef = doc(db, "users", userId);
        updatePromises.push(updateDoc(userRef, {
          groups: newGroups,
          updatedAt: new Date()
        }));
      });

      await Promise.all(updatePromises);

      // Update actual roles and groups in state
      setUsers(users.map(u => ({
        ...u,
        role: pendingChanges[u.id] || u.role,
        groups: pendingGroupChanges[u.id] || u.groups,
        pendingRole: null
      })));

      setPendingChanges({});
      setPendingGroupChanges({});
      
      const totalChanges = Object.keys(pendingChanges).length + Object.keys(pendingGroupChanges).length;
      alert(t('users.successfullyUpdated', { count: totalChanges }));
    } catch (error) {
      console.error("Error updating users:", error);
      alert(t('users.failedToUpdate'));
    } finally {
      setUpdating(null);
    }
  }

  async function handleRemoveUser(userId, userEmail) {
    if (userId === auth.currentUser?.uid) {
      alert(t('users.cannotRemoveSelf'));
      return;
    }

    if (!window.confirm(t('users.confirmRemoveUser', { email: userEmail }))) {
      return;
    }

    try {
      const functions = getFunctions();
      const deleteUser = httpsCallable(functions, 'deleteUser');
      
      const result = await deleteUser({ 
        userId: userId,
        companyId: company?.id 
      });
      
      alert(result.data.message || t('users.userRemovedSuccessfully'));
      loadUsers(); // Reload the user list
    } catch (error) {
      console.error("Error removing user:", error);
      alert(error.message || t('users.failedToRemoveUser'));
    }
  }

  async function addNewUser() {
    // Validate per login option
    if (newUserLoginOption === 'email' || newUserLoginOption === 'both') {
      if (!newUserEmail) {
        alert(t('users.pleaseEnterEmail'));
        return;
      }
    }
    if (newUserLoginOption === 'username' || newUserLoginOption === 'both') {
      if (!newUserUsername) {
        alert(t('users.pleaseEnterUsername'));
        return;
      }
    }
    if (!newUserPassword || newUserPassword.length < 6) {
      alert(t('users.passwordTooShort'));
      return;
    }

    // Check user limit
    const userLimit = company?.userLimit || 0;
    if (users.length >= userLimit) {
      alert(t('users.userLimitReached', { limit: userLimit }));
      return;
    }

    setAdding(true);

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        alert(t('users.companyNotFound'));
        return;
      }
      
      // Create user via Cloud Function (prevents logging out admin)
      const functions = getFunctions();
      const createUser = httpsCallable(functions, 'createUser');
      
      const display = newUserDisplayName || (newUserEmail ? newUserEmail.split('@')[0] : newUserUsername);
      const result = await createUser({
        email: newUserEmail || null,
        username: newUserUsername || null,
        loginOption: newUserLoginOption,
        password: newUserPassword,
        displayName: display,
        role: newUserRole,
        companyId: companyId
      });

      if (result.data.success) {
        // Upload image if provided
        let imageUrl = null;
        if (newUserImageFile && newUserImagePreview) {
          imageUrl = await uploadNewUserImage(companyId, result.data.userId);
        }

        alert(t('users.userCreatedSuccessfully', { user: newUserEmail || newUserUsername, role: getRoleLabel(newUserRole) }));
        
        // Reset form and reload users
        setNewUserEmail("");
        setNewUserUsername("");
        setNewUserLoginOption('email');
        setNewUserPassword("");
        setNewUserDisplayName("");
        setNewUserRole(USER_ROLES.USER);
        setNewUserImageFile(null);
        setNewUserImagePreview(null);
        setShowAddUserModal(false);
        loadUsers();
      } else {
        throw new Error(result.data.error || 'Failed to create user');
      }
    } catch (error) {
      console.error("Error creating user:", error);
      
      if (error.message?.includes('email-already-in-use')) {
        alert(t('users.emailAlreadyRegistered'));
      } else if (error.message?.includes('weak-password')) {
        alert(t('users.passwordTooShort'));
      } else if (error.message?.includes('invalid-email')) {
        alert(t('users.invalidEmail'));
      } else {
        alert(error.message || t('users.failedToCreateUser'));
      }
    } finally {
      setAdding(false);
    }
  }

  function getRoleLabel(role) {
    switch (role) {
      case USER_ROLES.ADMIN:
        return t('users.admin');
      case USER_ROLES.SUPER_USER:
        return t('users.superUser');
      case USER_ROLES.USER:
      default:
        return t('users.user');
    }
  }

  function getRoleColor(role) {
    switch (role) {
      case USER_ROLES.ADMIN:
        return "bg-fuchsia-500/15 text-fuchsia-100 border-fuchsia-300/40";
      case USER_ROLES.SUPER_USER:
        return "bg-blue-500/15 text-blue-100 border-blue-300/40";
      case USER_ROLES.USER:
      default:
        return "bg-white/10 text-white border-white/20";
    }
  }

  function getDisplayName(user) {
    const dn = user?.displayName;
    if (typeof dn === 'string' && dn.trim()) return dn.trim();
    if (dn && typeof dn === 'object') {
      const { fullName, name, firstName, lastName } = dn;
      const fromObject = [fullName, name, [firstName, lastName].filter(Boolean).join(' ')].find(
        val => typeof val === 'string' && val.trim()
      );
      if (fromObject) return fromObject.trim();
    }
    if (typeof user?.name === 'string' && user.name.trim()) return user.name.trim();
    return user?.email?.split('@')[0] || 'Unknown';
  }

  function getSecondaryLine(user) {
    if (typeof user?.email === 'string' && user.email.trim()) return user.email.trim();
    if (typeof user?.username === 'string' && user.username.trim()) return user.username.trim();
    return "";
  }

  const availableSlots = Math.max(0, (company?.userLimit || 0) - users.length);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white">
        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-10 py-8 shadow-2xl flex flex-col items-center">
          <div className="h-14 w-14 rounded-full border-2 border-white/40 border-t-transparent animate-spin" />
          <p className="mt-4 text-sm text-white/70">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white/90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Top Bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded-full border border-white/10 bg-white/10 hover:bg-white/20 text-white transition-all flex items-center gap-2 shadow-sm"
            >
              ← {t('common.back')}
            </button>
            <button
              onClick={() => window.location.href = "https://buildmychecks.co.uk/home"}
              className="px-4 py-2 rounded-full border border-emerald-400/30 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 transition-all flex items-center gap-2 shadow-sm"
            >
              🏠 {t('common.home')}
            </button>
          </div>
          <button
            onClick={() => setShowAddUserModal(true)}
            className="px-5 py-2.5 rounded-full bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 hover:shadow-lg hover:-translate-y-0.5 transition-all text-sm font-semibold flex items-center gap-2"
          >
            <span className="text-lg">＋</span> {t('users.addUser')}
          </button>
        </div>

        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-6 sm:p-8 mb-6">
          <div className="absolute -top-16 -right-10 h-48 w-48 bg-fuchsia-500/20 blur-3xl" />
          <div className="absolute -bottom-12 -left-10 h-52 w-52 bg-blue-500/20 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{t('users.manageRoles')}</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{t('users.userAccessRolesAndGroups')}</h1>
              <p className="mt-3 text-sm sm:text-base text-white/70 max-w-2xl">
                {t('users.adjustPermissionsDescription')}
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-white/70">
                <span className="px-3 py-1 rounded-full border border-white/15 bg-white/5">{t('users.adminOnly')}</span>
                <span className="px-3 py-1 rounded-full border border-white/15 bg-white/5">{t('users.changesApplyAfterRefresh')}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full md:w-auto">
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm">
                <p className="text-white/60">{t('users.activeUsers')}</p>
                <p className="text-2xl font-semibold text-white">{users.length}</p>
              </div>
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm">
                <p className="text-emerald-100/80">{t('users.openSlots')}</p>
                <p className="text-2xl font-semibold text-emerald-100">{availableSlots}</p>
                <p className="text-[11px] text-emerald-100/70 mt-1">{t('users.ofTotal', { total: company?.userLimit || 0 })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Role Descriptions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[{ label: t('users.admin'), icon: '👑', desc: [t('users.roleDescFullAccess'), t('users.roleDescCreateEditForms'), t('users.roleDescSchedulesApprovals'), t('users.roleDescManageUsers')], color: 'from-fuchsia-500/20 to-purple-500/10 border-fuchsia-400/20 text-fuchsia-50' }, { label: t('users.superUser'), icon: '⭐', desc: [t('users.roleDescSetSchedules'), t('users.roleDescApproveSubmissions'), t('users.roleDescConductChecks'), t('users.roleDescCannotManageUsers')], color: 'from-blue-500/20 to-cyan-500/10 border-blue-400/20 text-blue-50' }, { label: t('users.user'), icon: '👤', desc: [t('users.roleDescConductChecks'), t('users.roleDescViewSubmissions'), t('users.roleDescNoFormCreation'), t('users.roleDescNoScheduleChanges')], color: 'from-slate-400/20 to-slate-600/10 border-white/10 text-white/80' }].map((role, idx) => (
            <div key={idx} className={`rounded-2xl border bg-gradient-to-br ${role.color} p-4 shadow-lg backdrop-blur-md`}>
              <div className="flex items-center gap-2 text-sm uppercase tracking-wide text-white/70 mb-2">
                <span className="text-lg">{role.icon}</span>
                <span className="font-semibold">{role.label}</span>
              </div>
              <ul className="space-y-2 text-sm">
                {role.desc.map((line, lineIdx) => (
                  <li key={lineIdx} className="flex items-center gap-2 text-white/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Users */}
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
            <div>
              <p className="text-xs text-white/60">{t('users.teamDirectory')}</p>
              <h3 className="text-lg font-semibold text-white">{t('users.rolesAndGroups')}</h3>
            </div>
            {(Object.keys(pendingChanges).length > 0 || Object.keys(pendingGroupChanges).length > 0) && (
              <div className="flex items-center gap-3">
                <div className="text-xs text-amber-100/90 bg-amber-500/20 border border-amber-400/30 px-3 py-1 rounded-full">
                  {t('users.pendingChanges', { count: Object.keys(pendingChanges).length + Object.keys(pendingGroupChanges).length })}
                </div>
                <button
                  onClick={cancelChanges}
                  disabled={updating === "all"}
                  className="px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/10 disabled:opacity-50"
                >
                  {t('common.reset')}
                </button>
                <button
                  onClick={saveAllChanges}
                  disabled={updating === "all"}
                  className="px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-slate-950 font-semibold hover:shadow-lg disabled:opacity-50"
                >
                  {updating === "all" ? t('common.saving') : t('users.saveAll')}
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10 text-sm">
              <thead className="bg-white/5 text-white/70">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">{t('users.user')}</th>
                  <th className="px-6 py-3 text-left font-semibold">{t('users.email')}</th>
                  <th className="px-6 py-3 text-left font-semibold">{t('users.groups')}</th>
                  <th className="px-6 py-3 text-left font-semibold">{t('users.currentRole')}</th>
                  <th className="px-6 py-3 text-left font-semibold">{t('users.change')}</th>
                  <th className="px-6 py-3 text-left font-semibold">{t('users.action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-blue-500 text-white flex items-center justify-center font-semibold">
                          {user.email?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-semibold text-white">{getDisplayName(user)}</p>
                          {getSecondaryLine(user) && (
                            <p className="text-xs text-white/50">{getSecondaryLine(user)}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-white/80">{user.email || t('common.notAvailable')}</td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-wrap items-center gap-1">
                        {(pendingGroupChanges[user.id] || user.groups || []).length > 0 ? (
                          (pendingGroupChanges[user.id] || user.groups || []).map((group, idx) => (
                            <span key={idx} className="px-2 py-1 text-[11px] rounded-full bg-blue-400/15 text-blue-100 border border-blue-300/30">
                              {group}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-white/50">{t('users.noGroups')}</span>
                        )}
                        <button
                          onClick={() => setEditingGroups(editingGroups === user.id ? null : user.id)}
                          className="ml-1 px-2 py-1 text-[11px] rounded-full border border-white/10 text-white/80 hover:bg-white/10"
                        >
                          {editingGroups === user.id ? t('common.close') : t('common.edit')}
                        </button>
                      </div>
                      {editingGroups === user.id && company?.groups && (
                        <div className="mt-2 p-3 rounded-2xl border border-white/10 bg-white/5">
                          <p className="text-[11px] uppercase tracking-wide text-white/60 mb-2">{t('users.assignToGroups')}</p>
                          <div className="flex flex-wrap gap-2">
                            {company.groups.map((group, idx) => {
                              const userGroups = pendingGroupChanges[user.id] || user.groups || [];
                              const isAssigned = userGroups.includes(group);
                              return (
                                <button
                                  key={idx}
                                  onClick={() => handleGroupToggle(user.id, group)}
                                  className={`px-3 py-1 text-[11px] rounded-full border transition-all ${
                                    isAssigned
                                      ? 'bg-blue-500 text-white border-blue-400 shadow-md'
                                      : 'bg-white/5 text-white/80 border-white/10 hover:border-blue-300/40'
                                  }`}
                                >
                                  {isAssigned ? '✓ ' : ''}{group}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-[11px] font-semibold rounded-full border ${getRoleColor(user.role || USER_ROLES.USER)}`}>
                        {getRoleLabel(user.role || USER_ROLES.USER)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={user.pendingRole !== null ? user.pendingRole : (user.role || USER_ROLES.USER)}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={updating === "all"}
                        className={`text-sm border rounded-xl px-3 py-2 bg-slate-900/60 text-white/90 focus:ring-2 focus:ring-fuchsia-400 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.pendingRole !== null ? 'border-amber-400/70 bg-amber-500/10' : 'border-white/10'
                        }`}
                      >
                        <option value={USER_ROLES.USER}>{t('users.user')}</option>
                        <option value={USER_ROLES.SUPER_USER}>{t('users.superUser')}</option>
                        <option value={USER_ROLES.ADMIN}>{t('users.admin')}</option>
                      </select>
                      {user.pendingRole !== null && (
                        <span className="ml-2 text-[11px] text-amber-200 font-semibold">{t('users.pending')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.id !== auth.currentUser?.uid && (
                        <button
                          onClick={() => handleRemoveUser(user.id, user.email)}
                          className="text-red-200 hover:text-red-100 text-sm font-semibold"
                        >
                          {t('users.remove')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="px-6 py-12 text-center text-white/70">
              <p className="text-lg font-semibold">{t('users.noUsersFound')}</p>
              <p className="text-sm text-white/60 mt-2">{t('users.inviteFirstTeammate')}</p>
              <button
                onClick={() => setShowAddUserModal(true)}
                className="mt-4 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white"
              >
                {t('users.addUser')}
              </button>
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 text-sm text-white/80">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-500/15 border border-blue-400/30 flex items-center justify-center text-blue-100">ℹ️</div>
              <div>
                <p className="font-semibold text-white">{t('users.savingPermissions')}</p>
                <p className="text-white/70 text-sm">{t('users.savingPermissionsDescription')}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-white/70">
              <span className="px-3 py-1 rounded-full border border-white/10 bg-white/5">{t('users.adminOnly')}</span>
              <span className="px-3 py-1 rounded-full border border-white/10 bg-white/5">{t('users.approverGroupsLiveHere')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !adding && setShowAddUserModal(false)} />
          <div className="relative bg-slate-950/90 text-white rounded-3xl border border-white/10 shadow-2xl max-w-lg w-full p-6 overflow-hidden">
            <div className="absolute -top-16 -right-10 h-32 w-32 bg-fuchsia-500/20 blur-3xl" />
            <div className="absolute -bottom-12 -left-10 h-32 w-32 bg-blue-500/20 blur-3xl" />
            <div className="relative">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50">{t('users.invite')}</p>
                  <h3 className="text-2xl font-bold">{t('users.addNewUser')}</h3>
                  <p className="text-white/60 text-sm mt-1">{t('users.addNewUserDescription')}</p>
                </div>
                <button
                  onClick={() => !adding && setShowAddUserModal(false)}
                  className="text-white/60 hover:text-white"
                  disabled={adding}
                >
                  ✕
                </button>
              </div>

              {company && (
                <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-white/60">{t('users.userSlotsRemaining')}</p>
                    <p className="text-2xl font-semibold text-white">{availableSlots}</p>
                  </div>
                  <div className="text-right text-[11px] text-white/60">{t('users.maxSlots', { max: company?.userLimit || 0 })}</div>
                </div>
              )}

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.loginMethod')}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {[{ value: 'email', label: t('users.emailOnly') }, { value: 'username', label: t('users.usernameOnly') }, { value: 'both', label: t('users.emailAndUsername') }].map(option => (
                      <label key={option.value} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm ${newUserLoginOption === option.value ? 'border-fuchsia-400/60 bg-fuchsia-500/10 text-white' : 'border-white/10 bg-white/5 text-white/70'}`}>
                        <input type="radio" name="loginOption" value={option.value} checked={newUserLoginOption === option.value} onChange={(e) => setNewUserLoginOption(e.target.value)} />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {(newUserLoginOption === 'email' || newUserLoginOption === 'both') && (
                  <div>
                    <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.email')}</label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@company.com"
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                    />
                  </div>
                )}

                {(newUserLoginOption === 'username' || newUserLoginOption === 'both') && (
                  <div>
                    <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.username')}</label>
                    <input
                      type="text"
                      value={newUserUsername}
                      onChange={(e) => setNewUserUsername(e.target.value.replace(/\s+/g, ''))}
                      placeholder="johndoe"
                      className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                    />
                    <p className="text-[11px] text-white/50 mt-1">{t('users.usernameHint')}</p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.passwordMinChars')}</label>
                  <input
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="••••••"
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.displayName')}</label>
                  <input
                    type="text"
                    value={newUserDisplayName}
                    onChange={(e) => setNewUserDisplayName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                  />
                  <p className="text-[11px] text-white/50 mt-1">{t('users.displayNameHint')}</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.profileImage')}</label>
                  <div className="flex items-center gap-3">
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleNewUserImageChange} className="flex-1 text-xs text-white" />
                    {newUserImageFile && (
                      <button type="button" onClick={() => { setNewUserImageFile(null); setNewUserImagePreview(null); }} className="px-3 py-2 text-xs rounded-xl border border-red-400/50 text-red-100 hover:bg-red-500/20">
                        {t('users.remove')}
                      </button>
                    )}
                  </div>
                  {newUserImagePreview && (
                    <div className="mt-3">
                      <img src={newUserImagePreview} alt={t('users.preview')} className="h-16 w-16 rounded-xl object-cover border border-white/10" />
                      <p className="text-[11px] text-white/50 mt-1">{t('users.imageFormatHint')}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-2">{t('users.role')}</label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                  >
                    <option value={USER_ROLES.USER} className="bg-slate-900">{t('users.user')}</option>
                    <option value={USER_ROLES.SUPER_USER} className="bg-slate-900">{t('users.superUser')}</option>
                    <option value={USER_ROLES.ADMIN} className="bg-slate-900">{t('users.admin')}</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserEmail("");
                    setNewUserPassword("");
                    setNewUserUsername("");
                    setNewUserDisplayName("");
                    setNewUserRole(USER_ROLES.USER);
                    setNewUserImageFile(null);
                    setNewUserImagePreview(null);
                    setNewUserLoginOption('email');
                  }}
                  disabled={adding}
                  className="flex-1 px-4 py-3 rounded-xl border border-white/15 text-white hover:bg-white/10 disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={addNewUser}
                  disabled={adding || users.length >= (company?.userLimit || 0)}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adding ? t('users.creating') : users.length >= (company?.userLimit || 0) ? t('users.limitReached') : t('users.createUser')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
